import React, { useEffect } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  LabraToolTipV2,
  BoxV4,
  PlanType,
  RadioButtonsV2,
  RadioButtonType,
  NoteV2,
} from '@labrav/react-components'
import { Divider, Typography } from '@mui/material'
import { useStyles } from './AdditionalPaymentConfiguration.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { MultiCurrencyForm } from '../../../../common/components/MultiCurrencyForm/MultiCurrencyForm'
import { NetPaymentTermsField } from '../../../../common/components/NetPaymentTermsField/NetPaymentTermsField'
import { useSelector } from '../../../../store'
import { FlyOutListingData } from '../../../../types/flyout'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import { AgreementDurationType, DiscountType } from '../../../../types/flyout'
import { ScheduleType } from '../../../../modules/privateOffer/types'
import type { AdditionalPaymentConfigurationProps } from '../../types'

export type { AdditionalPaymentConfigurationProps }

export const AdditionalPaymentConfiguration: React.FC<
  AdditionalPaymentConfigurationProps
> = ({ product, noteDescription }) => {
  const { values, setFieldValue } =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const formikProps = useFormikContext() as FormikProps<Record<string, unknown>>
  const flyoutSellerInfo = useSelector<FlyOutListingData>(
    state => state.flyoutSellerInfo
  )
  const isSaasSubscription = product?.productType === PlanType.SAAS_SUBSCRIPTION

  useEffect(() => {
    if (isSaasSubscription) {
      setFieldValue('discountType', DiscountType.USAGE_PRICING)
    }
  }, [isSaasSubscription, setFieldValue])

  const handlePricingModelTypeChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const clickedKey = e.target.value
    if (clickedKey === DiscountType.FLEXIBLE_PAYMENT_TERMS) {
      setFieldValue('installmentInfo', [
        { paymentDate: '', amount: null, currency: 'USD' },
      ])
      if (!values.flexiblePaymentType) {
        setFieldValue('flexiblePaymentType', ScheduleType.MONTHLY)
      }
      setFieldValue('agreementDurationType', AgreementDurationType.SINGLE_USE)
    } else {
      setFieldValue('installmentInfo', [])
      setFieldValue('tcv', null)
      setFieldValue('firstInstallmentDate', null)
    }
    if (!clickedKey) {
      await setFieldValue('discountType', values.discountType)
      return
    }

    await setFieldValue('discountType', clickedKey)
  }

  const { classes } = useStyles()
  return (
    <div data-testid="flexible-section">
      <BoxV4
        heading={'Currency and Billing configuration'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        <MultiCurrencyForm
          formik={
            formikProps as unknown as FormikProps<Record<string, unknown>>
          }
          flyoutSellerInfo={flyoutSellerInfo}
          headerText="Select currency"
          infoText="Set the transaction currency for this authorization. The currency must be fully configured in AMMP for successful creation. The reseller will be able to create a channel offer only in this selected currency."
          subTitle={
            <NoteV2
              description={
                <Typography className={classes.noteText}>
                  {noteDescription && <span>{noteDescription} </span>}
                  Select a currency that is already configured in AMMP. Currency
                  with incomplete setup will lead to reseller authorization
                  failure.
                </Typography>
              }
            />
          }
          selectInfoText="Authorization will be created in this selected currency."
        />
        <Divider className={classes.divider} />
        <NetPaymentTermsField
          context="resaleAuthorization"
          formik={
            formikProps as unknown as FormikProps<Record<string, unknown>>
          }
        />
        <Divider className={classes.divider} />

        <div data-testid="ami-pricing-section">
          <div className={classes.subSectionContainer}>
            <div className={classes.inLineInfo}>
              <Typography className={classes.headingText}>
                Pricing model
              </Typography>
              <LabraToolTipV2
                tooltipPlacement="top"
                description="Select whether the buyer will be billed upfront, through installments, or based on their usage."
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className={classes.info}
                  data-testid="prerequisites-config-tooltip"
                />
              </LabraToolTipV2>
            </div>
            <div data-testid="ami-pricing-model-options">
              <RadioButtonsV2
                radioButtonMappings={[
                  {
                    key: DiscountType.INDIVIDUAL_PRICING,
                    label: 'Upfront payment',
                    type: RadioButtonType.STANDARD,
                    disabled: isSaasSubscription,
                  },
                  {
                    key: DiscountType.FLEXIBLE_PAYMENT_TERMS,
                    label: 'Installment plan',
                    type: RadioButtonType.STANDARD,
                    disabled: isSaasSubscription,
                  },
                  {
                    key: DiscountType.USAGE_PRICING,
                    label: 'Usage pricing',
                    type: RadioButtonType.STANDARD,
                    disabled: !isSaasSubscription,
                    infoDescription: !isSaasSubscription
                      ? 'Usage pricing is disabled for SaaS contract products.'
                      : '',
                  },
                ]}
                checkedValue={{
                  key: values.discountType || '',
                  value: values.discountType || '',
                }}
                handleChange={handlePricingModelTypeChange}
                gapBetween="32px"
              />
            </div>
          </div>
        </div>
      </BoxV4>
    </div>
  )
}
